import React, { ReactElement, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image, Pressable, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import Moment from 'moment';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import EditUser from './EditUser';
import CheckBox from '../16pxIcons/CheckBox';
import AlertModal from '../AlertModal';
import deleteUser from '../../requests/Users/DeleteUser';
import { logEvent } from '../LogEvent';
import ReInviteIcon24 from '../24pxIcons/ReInviteIcon24';
import { PressableState } from '../PressableStyled';
import Style from '../../constants/Style';
import reinviteUser from '../../requests/Users/ReinviteUser';
import RoleModal from './RoleModal';
import { TAG_COLORS, UserPermission } from '../../constants/Types';
import EllipsisIconVertical from '../EllipsisIconVertical';
import DeleteIcon24 from '../24pxIcons/DeleteIcon24';
import updateUserGroupUsers from '../../requests/Users/UpdateUserGroupUsers';

interface UserRowWebProps {
  user,
  selectable?: boolean,
  // eslint-disable-next-line no-unused-vars
  showError?: (message?: string) => void,
  userDeleted?: () => void,
  roleChanged?: () => void,
  roles?: object[],
  userGroups?: { Name: string, UserGroupId: string }[],
  selectedGroupId?: string,
  contextIndexId?: string,
  // eslint-disable-next-line no-unused-vars
  setContextIndexId?: (id: string) => void,
}

export default function UserRowWebV2({
  user, selectable, showError, userDeleted, roleChanged, roles, userGroups, selectedGroupId,
  contextIndexId, setContextIndexId,
}: UserRowWebProps) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const canUpdateUsers: boolean = isAdmin || permissions.includes(UserPermission.USERS_UPDATE);
  const canDeleteUsers: boolean = isAdmin || permissions.includes(UserPermission.USERS_DELETE);
  const canViewRoles: boolean = isAdmin || permissions.includes(UserPermission.ROLES_READ);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [roleModalVisible, setRoleModalVisible] = useState<boolean>(false);
  const [selected, setSelected] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [reinviteModalVisible, setReinviteModalVisible] = useState<boolean>(false);
  const [contextMenuVisible, setContextMenuVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (contextIndexId !== user.id) {
      setContextMenuVisible(false);
    }
  }, [contextIndexId]);

  const generateColor = (first: number, last: number) => {
    const randomColor = Math.floor((1 / (first + last)) * 16777215)
      .toString(16)
      .padStart(6, '0');
    return `#${randomColor}`;
  };

  const reinvite = () => {
    setLoading(true);
    reinviteUser(user.email).then(() => {
      setReinviteModalVisible(true);
      setLoading(false);
    })
      .catch(() => {
        showError('');
        setLoading(false);
      });
  };

  const renderUserIcon = (imageSource: string, facilityManager: boolean = false) => {
    if (imageSource === undefined) {
      return (
        <View
          style={{
            width: 24,
            height: 24,
            borderRadius: 12,
            marginRight: 8,
            backgroundColor: generateColor(
              user?.firstName ? user?.firstName?.length : user?.email?.length,
              user?.lastName ? user?.lastName?.length : user?.email?.length,
            ),
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            style={{
              color: '#fff',
              fontFamily: 'Roboto_400Regular',
              fontSize: 8,
            }}
            selectable
          >
            {`${(user?.firstName ? user?.firstName : user?.email)?.charAt(0)?.toUpperCase() ?? ''}${user?.lastName?.charAt(0)?.toUpperCase() ?? ''}`}
          </Text>
        </View>
      );
    }
    return (
      <View
        style={{
          width: 48,
          height: 48,
          backgroundColor: facilityManager ? '#fff' : 'transparent',
          borderWidth: 1.5,
          borderColor: facilityManager ? Colors.light.hereworksBlue500 : 'transparent',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 24,
          marginLeft: -14,
        }}
        key={user.email}
      >
        <Image
          style={styles.userPic}
          source={{ uri: imageSource }}
        />
      </View>
    );
  };

  const pending: boolean = user.status === 'PENDING_VERIFICATION';

  const userName: string = (user.firstName || user.lastName)
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}` : user.email;

  const getRoleTitle = (): string => {
    if (user.roleList?.length) {
      return user.roleList[0].name;
    }
    return user.isAdmin ? 'Admin' : 'Non Admin';
  };

  return (
    <View style={{
      // flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      zIndex: contextMenuVisible ? 10 : 1,
      borderWidth: 1,
      borderColor: Colors.light.hereworksWhite300,
      borderRadius: 8,
      marginVertical: 4,
    }} key={user.email}>
      <EditUser
        editModalVisible={editModalVisible}
        {...{ user, roleChanged, setEditModalVisible }}
        imageSource={user.icon}
        facilityManager={user.facilityManager}
        roles={roles}
      />
      <View
        style={{
          marginLeft: 22,
          flex: 1,
          flexDirection: 'row',
          marginVertical: 8,
          alignItems: 'center',
        }}
      >
        {selectable && (
          <TouchableOpacity
            onPress={() => setSelected(!selected)}
            style={{ marginRight: 30, justifyContent: 'center' }}
          >
            <CheckBox
              checkcolor={selected ? null : '#fff'}
              color={selected ? null : Colors.light.hereworksBlue100}
            />
          </TouchableOpacity>
        )}
        {renderUserIcon(user.icon, user.facilityManager)}
        <View style={{ marginLeft: 4, flex: 1 }}>
          <Text numberOfLines={1} style={styles.nameStyle}>
            {userName}
          </Text>
        </View>
      </View>
      <View style={{ flex: 1 }}>
        {(user.roleList?.length) ? <>
          {user.roleList.map((role, index) => (
            <Pressable style={{
              flexDirection: 'row',
              alignItems: 'center',
              alignSelf: 'flex-start',
              backgroundColor: TAG_COLORS[role.color || 'blue' as keyof typeof TAG_COLORS].background,
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 65,
            }}
              onPress={() => setRoleModalVisible(true)}
              disabled={!canViewRoles}
            >
              <Text key={role.id} style={{
                fontFamily: 'Poppins_400Regular',
                color: TAG_COLORS[role.color || 'blue' as keyof typeof TAG_COLORS].text,
                fontSize: 12,
              }}>
                {role.name}{index < user.roleList.length - 1 ? ', ' : ''}
              </Text>
            </Pressable>
          ))}</> : <Pressable
            onPress={() => setRoleModalVisible(true)}
            disabled={!canViewRoles}
          >
          <Text style={[styles.userDetails, {
            alignSelf: 'flex-start',
            fontFamily: 'Poppins_400Regular',
            fontSize: 12,
            backgroundColor: Colors.light.hereworksBlue50,
            color: Colors.light.hereworksBlue600,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 65,
          }]}>
            {user.isAdmin ? 'Admin' : 'Non Admin'}
          </Text></Pressable>}
      </View>
      {(!selectedGroupId && !pending) && <View style={{ flex: 1 }}>
        {(user.userGroupMemberships.length && !selectedGroupId) ? <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginTop: 6,
            flexWrap: 'wrap',
          }}>
          {user.userGroupMemberships?.map((group) => (
            <View key={group.userGroupId}>
              <Text key={group.userGroupId} style={{
                marginRight: 4,
                fontFamily: 'Poppins_400Regular',
                fontSize: 12,
                lineHeight: 18,
                backgroundColor: Colors.light.hereworksDarkBlue,
                color: '#fff',
                paddingHorizontal: 8,
                paddingVertical: 2,
                borderRadius: 4,
                marginVertical: 2,
              }}>
                {
                  userGroups?.find(
                    (userGroup: any) => userGroup.UserGroupId === group.userGroupId,
                  )?.Name
                }
              </Text>
            </View>
          ))}
        </View> : null}
      </View>}
      <View style={{ flex: 1, marginRight: 30 }}>
        <Text style={[styles.userDetails]} selectable>{user.email}</Text>
      </View>
      {/* {!pending && <View style={{ flex: 1 }}>
        <Text style={[styles.userDetails]}>
          {user.LastAuthenticatedOn
            ? Moment(user.LastAuthenticatedOn).format('ll')
            : 'No activity yet'}
        </Text>
      </View>} */}
      {pending && <View style={styles.statusLabelContainer}>
        <Text style={[
          styles.statusLabel,
          styles.statusPending,
        ]}>
          {pending ? 'Pending' : 'Accepted'}
        </Text>
      </View>}
      {((selectedGroupId && !canDeleteUsers)
        || (!selectedGroupId && !(canUpdateUsers || canDeleteUsers))) && <View
        style={selectedGroupId ? { width: 104 } : { flex: 1 }}>
        <Text style={[styles.userDetails, { alignSelf: 'flex-end', marginLeft: 34 }]}>
          {Moment(user.LastAuthenticatedOn).format('ll')}
        </Text>
      </View>}
      <View style={selectedGroupId && canDeleteUsers
        ? { width: 104 }
        : { position: 'absolute', right: 0 }}>
        <View style={styles.buttonContainer}>
          <>
            {pending && <Pressable
              onPress={() => {
                reinvite();
                logEvent('UserRowWeb', 'Re-invite');
              }}
              testID='um-re-invite-user-role-btn'
            >
              {({ hovered }: PressableState): ReactElement => (
                <View
                  style={{
                    backgroundColor: hovered ? Colors.light.hereworksBlue500 : 'transparent',
                    borderRadius: 4,
                  }}
                >
                  <ReInviteIcon24 color={hovered ? '#fff' : null} />
                </View>
              )}
            </Pressable>}
            {(canUpdateUsers && !selectedGroupId) && <Pressable
              style={{ marginLeft: 12, zIndex: 5 }}
              onPress={() => {
                if (!contextMenuVisible) {
                  setContextIndexId(user.id);
                }
                setContextMenuVisible(!contextMenuVisible);
              }}
              testID='um-user-context-menu-btn'
            >
              {({ hovered }: PressableState): ReactElement => <View
                style={{
                  backgroundColor: hovered ? Colors.light.hereworksWhite200 : 'transparent',
                  borderRadius: 4,
                  width: 24,
                  height: 24,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <EllipsisIconVertical />
              </View>}
            </Pressable>}
            {(canDeleteUsers && selectedGroupId) && <Pressable
              style={{ marginLeft: 12 }}
              onPress={() => {
                logEvent('UserRowWeb', 'Delete from Group');
                setDeleteModalVisible(true);
              }}
              testID='um-delete-user-from-group-btn'
            >
              {({ hovered }: PressableState): ReactElement => (
                <View
                  style={{
                    backgroundColor: hovered ? Colors.light.hereworksRed50 : 'transparent',
                    borderRadius: 4,
                    padding: 8,
                  }}
                >
                  <DeleteIcon24 />
                </View>
              )}
            </Pressable>}
            {contextMenuVisible && <View
              style={{
                position: 'absolute',
                zIndex: 10,
                top: 30,
                right: 0,
                backgroundColor: '#fff',
                borderRadius: 4,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.25,
                shadowRadius: 3.84,
                elevation: 5,
              }}
            ><Pressable
              style={{}}
              onPress={() => {
                setEditModalVisible(true);
                setContextIndexId(null);
                logEvent('UserRowWeb', 'Edit');
              }}
              testID='um-edit-user-role-btn'
            >
                {({ hovered }: PressableState): ReactElement => <View
                  style={{
                    backgroundColor: hovered ? Colors.light.hereworksWhite200 : 'transparent',
                    borderRadius: 4,
                    padding: 12,
                  }}
                >
                  <Text>Edit</Text>
                </View>}
              </Pressable>
              {canDeleteUsers && <Pressable
                style={{}}
                onPress={() => {
                  logEvent('UserRowWeb', 'Delete');
                  setContextIndexId(null);
                  setDeleteModalVisible(true);
                }}
                testID='um-delete-user-btn'
              >
                {({ hovered }: PressableState): ReactElement => <View
                  style={{
                    backgroundColor: hovered ? Colors.light.hereworksRed50 : 'transparent',
                    borderRadius: 4,
                    padding: 12,
                  }}
                >
                  <Text>Delete</Text>
                </View>}
              </Pressable>}
            </View>}
          </>
        </View>
      </View>
      <AlertModal
        modalVisible={deleteModalVisible}
        setModalVisible={setDeleteModalVisible}
        title={selectedGroupId ? 'Remove User from Group' : 'Delete User'}
        message={
          <>
            {`Are you sure you want to ${selectedGroupId ? 'remove' : 'delete'} the user `}
            <Text style={{ fontFamily: 'Roboto_700Bold' }}>{userName}</Text>
            {'?'}
          </>
        }
        actionTitle={selectedGroupId ? 'Remove' : 'Delete'}
        action={() => {
          setLoading(true);
          if (selectedGroupId) {
            // Remove user from group
            updateUserGroupUsers(
              selectedGroupId,
              [],
              [user.id],
              userGroups.find((ug) => ug.UserGroupId === selectedGroupId)?.Name || '',
              buildingId,
            ).then((res) => {
              if (res?.isError) {
                showError(res?.message);
              } else {
                userDeleted();
              }
            }).finally(() => setLoading(false));
            return;
          }
          deleteUser(user.email).then((res) => {
            if (res?.isError) {
              showError(res?.message);
            } else {
              userDeleted();
            }
          }).finally(() => {
            setLoading(false);
          });
        }}
      />
      <AlertModal
        modalVisible={reinviteModalVisible}
        setModalVisible={setReinviteModalVisible}
        title={'Success!'}
        message={<>
          {'New invitation sent to '}
          <Text style={Style.alertBoldText}>{userName}</Text>
        </>}
        error={false}
      />
      <RoleModal
        permissions={user.roleList?.length ? user.roleList[0]?.permissions : null}
        isAdmin={user.isAdmin}
        isVisible={roleModalVisible}
        setModalVisible={setRoleModalVisible}
        title={getRoleTitle()}
      />
      {loading && <View style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.6)',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 8,
      }}>
        <ActivityIndicator size='small' color={Colors.light.hereworksBlue500} />
      </View>}
    </View>
  );
}

const styles = StyleSheet.create({
  userDetails: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
  },
  nameStyle: {
    marginLeft: 10,
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
    color: Colors.light.hereworksBlack600,
  },
  userPic: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: 'hidden',
  },
  statusLabelContainer: {
    alignSelf: 'center',
    flex: 1,
  },
  statusLabel: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
  },
  statusPending: {
    borderColor: Colors.light.hereworksYellow900,
    backgroundColor: Colors.light.hereworksYellow50,
    borderRadius: 4,
    borderWidth: 1,
    paddingHorizontal: 8,
    alignSelf: 'flex-start',
  },
  buttonContainer: {
    flexDirection: 'row',
    marginRight: 8,
    width: 96,
    alignSelf: 'flex-end',
    justifyContent: 'flex-end',
  },
});
